import { Component, Input, OnInit } from "@angular/core";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { ClientNoteViewModel } from "@app/shared/models/clients/ClientNoteViewModel.model";
import { DriverService } from "@app/driver/shared/driver.service";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

@Component({
  selector: "app-client-notes",
  templateUrl: "./client-notes.component.html",
  styleUrls: ["./client-notes.component.css"],
})
export class ClientNotesComponent implements OnInit {
  @Input() client: any;
  @Input() canEdit: boolean = false;

  notes: ClientNoteViewModel[] = [];
  filteredNotes: ClientNoteViewModel[] = [];
  showAddModal: boolean = false;
  showEditModal: boolean = false;
  newNote: ClientNoteViewModel = this.createEmptyNote();
  editingNote: ClientNoteViewModel | null = null;
  searchText: string = "";
  filterStatus: string = "";
  filterType: string = "";
  filterDateFrom: string = "";
  filterDateTo: string = "";

  noteTypes: string[] = ["General", "Accident", "Breakdown"];
  noteStatuses: string[] = ["Open", "Resolved"];

  // Team driver email state
  noteForEmail: ClientNoteViewModel | null = null;
  showTeamSelectionDialog: boolean = false;
  schedulingTeamsForEmail: any[] = [];
  schedulingTeamsLoading: boolean = false;
  selectedTeamIds: Set<string> = new Set<string>();
  selectedDriverIds: Set<string> = new Set<string>();
  showEmailPreview: boolean = false;
  emailPreview = { to: "", ccEmails: "", bccEmails: "", subject: "", body: "" };
  emailSending: boolean = false;
  emailValidationError: string = "";

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private driverService: DriverService,
    private featureFlags: FeatureFlagsService
  ) {}

  ngOnInit() {
    if (this.client?.id) {
      this.loadNotes();
    }
  }

  createEmptyNote(): ClientNoteViewModel {
    return {
      clientId: this.client?.id || "",
      note: "",
      status: "Open",
      type: "General",
      dateCreatedUtc: new Date(),
    };
  }

  loadNotes() {
    if (this.client.notes && Array.isArray(this.client.notes)) {
      this.notes = [...this.client.notes];
      // Sort by creation date descending (newest first)
      this.notes.sort(
        (a, b) =>
          new Date(b.dateCreatedUtc || 0).getTime() - new Date(a.dateCreatedUtc || 0).getTime()
      );
    } else {
      this.notes = [];
    }
    this.filterNotes();
  }

  filterNotes() {
    let filtered = [...this.notes];

    // Filter by status
    if (this.filterStatus) {
      filtered = filtered.filter((note) => note.status === this.filterStatus);
    }

    // Filter by type
    if (this.filterType) {
      filtered = filtered.filter((note) => note.type === this.filterType);
    }

    // Filter by search text
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(
        (note) =>
          note.note.toLowerCase().includes(searchLower) ||
          note.type.toLowerCase().includes(searchLower) ||
          note.status.toLowerCase().includes(searchLower)
      );
    }

    // Filter by date range
    if (this.filterDateFrom || this.filterDateTo) {
      filtered = filtered.filter((note) => {
        const noteDate = note.dateCreatedUtc ? new Date(note.dateCreatedUtc) : null;
        if (!noteDate) return false;

        // Get local date string (YYYY-MM-DD) for comparison
        const noteDateStr = noteDate.toLocaleDateString("en-CA"); // Returns YYYY-MM-DD format
        if (this.filterDateFrom && noteDateStr < this.filterDateFrom) {
          return false;
        }
        if (this.filterDateTo && noteDateStr > this.filterDateTo) {
          return false;
        }
        return true;
      });
    }

    this.filteredNotes = filtered;
  }

  openAddModal() {
    this.newNote = this.createEmptyNote();
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
    this.newNote = this.createEmptyNote();
  }

  openEditModal(note: ClientNoteViewModel) {
    this.editingNote = { ...note };
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingNote = null;
  }

  saveNote() {
    if (!this.validateNote(this.newNote)) return;

    this.http.post("api/client/notes/save", this.newNote).subscribe({
      next: (savedNote: any) => {
        this.notes.unshift(savedNote);
        this.filterNotes();
        this.toastr.success("Note saved successfully");
        this.closeAddModal();
      },
      error: (error) => {
        this.toastr.error("Error saving note");
        console.error(error);
      },
    });
  }

  updateNote() {
    if (!this.editingNote || !this.validateNote(this.editingNote)) return;

    this.http.post("api/client/notes/save", this.editingNote).subscribe({
      next: (updatedNote: any) => {
        const index = this.notes.findIndex((n) => n.id === updatedNote.id);
        if (index !== -1) {
          this.notes[index] = updatedNote;
          this.filterNotes();
        }
        this.toastr.success("Note updated successfully");
        this.closeEditModal();
      },
      error: (error) => {
        this.toastr.error("Error updating note");
        console.error(error);
      },
    });
  }

  deleteNote(note: ClientNoteViewModel) {
    if (!confirm("Are you sure you want to delete this note?")) return;

    this.http.delete(`api/client/notes/${note.id}`).subscribe({
      next: () => {
        this.notes = this.notes.filter((n) => n.id !== note.id);
        this.filterNotes();
        this.toastr.success("Note deleted successfully");
      },
      error: (error) => {
        this.toastr.error("Error deleting note");
        console.error(error);
      },
    });
  }

  validateNote(note: ClientNoteViewModel): boolean {
    if (!note.type) {
      this.toastr.warning("Note type is required");
      return false;
    }
    if (!note.status) {
      this.toastr.warning("Note status is required");
      return false;
    }
    if (!note.note || note.note.trim() === "") {
      this.toastr.warning("Note content is required");
      return false;
    }
    return true;
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "open":
        return "status-open";
      case "in progress":
        return "status-inprogress";
      case "resolved":
        return "status-resolved";
      case "closed":
        return "status-closed";
      default:
        return "";
    }
  }

  getHighlightedText(text: string): string {
    if (!this.searchText || !text) {
      return text || "";
    }

    const re = new RegExp(this.searchText, "gi");
    return text.replace(re, "<mark>$&</mark>");
  }

  // ── Team driver email ────────────────────────────────────────────────────

  openEmailTeamDrivers(note: ClientNoteViewModel) {
    this.noteForEmail = note;
    this.selectedTeamIds = new Set<string>();
    this.selectedDriverIds = new Set<string>();
    this.schedulingTeamsForEmail = [];
    this.showTeamSelectionDialog = true;
    this.schedulingTeamsLoading = true;
    this.driverService.getSchedulingTeamsByClient(this.client.id).subscribe(
      (teams: any[]) => {
        this.schedulingTeamsForEmail = teams || [];
        this.schedulingTeamsLoading = false;
        // Auto-select all teams and drivers that have emails
        for (const team of this.schedulingTeamsForEmail) {
          this.selectedTeamIds.add(team.id);
          for (const driver of team.drivers || []) {
            if (driver.email) {
              this.selectedDriverIds.add(driver.driverId || driver.id);
            }
          }
        }
      },
      () => {
        this.schedulingTeamsLoading = false;
        this.toastr.error("Failed to load scheduling teams.");
      }
    );
  }

  toggleTeamSelection(teamId: string) {
    const team = this.schedulingTeamsForEmail.find((t) => t.id === teamId);
    if (!team) return;
    if (this.selectedTeamIds.has(teamId)) {
      this.selectedTeamIds.delete(teamId);
      for (const driver of team.drivers || []) {
        this.selectedDriverIds.delete(driver.driverId || driver.id);
      }
    } else {
      this.selectedTeamIds.add(teamId);
      for (const driver of team.drivers || []) {
        if (driver.email) {
          this.selectedDriverIds.add(driver.driverId || driver.id);
        }
      }
    }
  }

  toggleDriverSelection(driverId: string) {
    if (this.selectedDriverIds.has(driverId)) {
      this.selectedDriverIds.delete(driverId);
    } else {
      this.selectedDriverIds.add(driverId);
    }
  }

  get selectedDriversForEmail(): any[] {
    const result: any[] = [];
    for (const team of this.schedulingTeamsForEmail) {
      if (!this.selectedTeamIds.has(team.id)) continue;
      for (const driver of team.drivers || []) {
        const id = driver.driverId || driver.id;
        if (this.selectedDriverIds.has(id) && driver.email) {
          if (!result.some((d) => (d.driverId || d.id) === id)) {
            result.push(driver);
          }
        }
      }
    }
    return result;
  }

  cancelTeamSelection() {
    this.showTeamSelectionDialog = false;
    this.noteForEmail = null;
  }

  proceedWithTeamDriverEmail() {
    if (!this.noteForEmail) return;
    const drivers = this.selectedDriversForEmail;
    if (!drivers.length) return;
    const bccEmails = drivers.map((d) => d.email).join(";");
    const clientName = this.client?.companyName || "Client";
    this.emailPreview = {
      to: "omahadispatch@lgttransport.com",
      ccEmails: "",
      bccEmails: bccEmails,
      subject: `Client Note - ${this.noteForEmail.type} - ${clientName}`,
      body: `Note Type: ${this.noteForEmail.type}\nStatus: ${this.noteForEmail.status}\n\n${this.noteForEmail.note}`,
    };
    this.emailValidationError = "";
    this.showTeamSelectionDialog = false;
    this.showEmailPreview = true;
  }

  cancelEmailPreview() {
    this.showEmailPreview = false;
    this.noteForEmail = null;
  }

  confirmSendEmail() {
    if (!this.emailPreview.to?.trim()) {
      this.emailValidationError = "Recipient email is required.";
      return;
    }
    this.emailValidationError = "";
    this.emailSending = true;
    this.http
      .post("api/client/notes/sendEmail", {
        to: this.emailPreview.to,
        ccEmails: this.emailPreview.ccEmails || null,
        bccEmails: this.emailPreview.bccEmails || null,
        subject: this.emailPreview.subject,
        body: this.emailPreview.body,
      })
      .subscribe({
        next: () => {
          this.emailSending = false;
          this.showEmailPreview = false;
          this.noteForEmail = null;
          this.toastr.success("Email sent successfully.");
        },
        error: (err) => {
          this.emailSending = false;
          this.toastr.error(err?.error?.error || "Failed to send email.");
        },
      });
  }
}
